import { useEffect, useState, useCallback } from 'react';
import {
  CreditCard,
  Check,
  ExternalLink,
  FileText,
  Download,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Cpu,
  Zap,
  HardDrive,
  Wifi,
  Calendar,
  DollarSign,
  Shield,
  RefreshCw,
  TrendingUp,
  Plus,
  Wallet,
  Settings,
  RotateCcw,
  Power,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Activity,
} from 'lucide-react';
import { billingApi, BillingAccount, Invoice, PaymentMethod, MonthlyBilling, AutoRechargeSettings, sellerApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import clsx from 'clsx';

// ─── Tab type ────────────────────────────────────────────────────────────────
type BillingTab = 'overview' | 'history';

// ─── Payment Method Card ─────────────────────────────────────────────────────
function PaymentMethodCard({
  paymentMethod,
  onManage,
  stripeConfigured,
}: {
  paymentMethod: PaymentMethod | null;
  onManage: () => void;
  stripeConfigured: boolean;
}) {
  const brandIcons: Record<string, string> = {
    visa: 'VISA',
    mastercard: 'MC',
    amex: 'AMEX',
    discover: 'DISC',
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-foreground-secondary" />
          <h3 className="font-semibold text-foreground">Payment Method</h3>
        </div>
        {stripeConfigured && (
          <button onClick={onManage} className="btn-ghost text-sm">
            {paymentMethod ? 'Update' : 'Add Card'}
          </button>
        )}
      </div>

      {!stripeConfigured ? (
        <div className="text-center py-4">
          <AlertCircle className="w-8 h-8 text-warning mx-auto mb-2" />
          <p className="text-sm text-foreground-secondary">Stripe not configured</p>
          <p className="text-xs text-foreground-tertiary mt-1">
            Set STRIPE_SECRET_KEY in your .env file to enable payments
          </p>
        </div>
      ) : paymentMethod ? (
        <div className="flex items-center gap-4">
          <div className="w-14 h-10 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {brandIcons[paymentMethod.brand] || paymentMethod.brand.toUpperCase().slice(0, 4)}
            </span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} ending in {paymentMethod.last4}
            </p>
            <p className="text-xs text-foreground-tertiary">
              Expires {String(paymentMethod.expMonth).padStart(2, '0')}/{paymentMethod.expYear}
            </p>
          </div>
          <Shield className="w-4 h-4 text-success" />
        </div>
      ) : (
        <div className="text-center py-4">
          <CreditCard className="w-8 h-8 text-foreground-muted mx-auto mb-2" />
          <p className="text-sm text-foreground-secondary">No payment method on file</p>
          <p className="text-xs text-foreground-tertiary mt-1">
            Add a card to enable adding funds
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Monthly Billing Row ─────────────────────────────────────────────────────
function MonthlyBillingRow({
  month,
  isExpanded,
  onToggle,
}: {
  month: MonthlyBilling;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const handleDownloadPdf = (e: React.MouseEvent) => {
    e.stopPropagation();
    billingApi.downloadMonthlyPdf(month.billingPeriod);
  };

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-background-hover transition-colors text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Calendar className="w-4 h-4 text-foreground-secondary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">{month.label}</span>
              {month.isCurrent && (
                <span className="badge-accent text-[10px] px-1.5">Current</span>
              )}
              {!month.isCurrent && !month.isFinal && (
                <span className="badge-warning text-[10px] px-1.5">Processing</span>
              )}
            </div>
            <span className="text-xs text-foreground-tertiary">Pay-As-You-Go</span>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-6 pl-7 sm:pl-0">
          {month.invoiceStatus && (
            <span
              className={clsx(
                'text-xs px-2 py-0.5 rounded-full font-medium',
                month.invoiceStatus === 'PAID' && 'bg-success/10 text-success',
                month.invoiceStatus === 'PENDING' && 'bg-warning/10 text-warning',
                month.invoiceStatus === 'FAILED' && 'bg-error/10 text-error'
              )}
            >
              {month.invoiceStatus}
            </span>
          )}

          <div className="text-right">
            <span className="text-sm font-semibold text-foreground tabular-nums">
              ${month.totalCost.toFixed(6)}
            </span>
          </div>

          <button
            onClick={handleDownloadPdf}
            className="p-1.5 hover:bg-background-hover rounded-md transition-colors text-foreground-secondary hover:text-accent"
            title="Download monthly statement PDF"
          >
            <Download className="w-4 h-4" />
          </button>

          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-foreground-secondary" />
          ) : (
            <ChevronDown className="w-4 h-4 text-foreground-secondary" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-5 bg-background-secondary/50 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Usage Breakdown
              </h4>
              <UsageItem
                icon={<Cpu className="w-4 h-4" />}
                label="GPU Compute"
                usage={`${month.gpuComputeHours} hrs`}
                cost={month.gpuComputeCost}
              />
              <UsageItem
                icon={<Zap className="w-4 h-4" />}
                label="API Calls"
                usage={month.apiCalls.toLocaleString()}
                cost={month.apiCallsCost}
              />
              <UsageItem
                icon={<HardDrive className="w-4 h-4" />}
                label="Storage"
                usage={`${month.storageGb} GB`}
                cost={month.storageCost}
              />
              <UsageItem
                icon={<Wifi className="w-4 h-4" />}
                label="Bandwidth"
                usage={`${month.bandwidthGb} GB`}
                cost={month.bandwidthCost}
              />
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Cost Summary
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-secondary">Total usage charges</span>
                  <span className="text-foreground tabular-nums">${month.totalCost.toFixed(6)}</span>
                </div>
                <div className="border-t border-border pt-2 mt-2 flex justify-between">
                  <span className="text-sm font-semibold text-foreground">Total</span>
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    ${month.totalCost.toFixed(6)}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleDownloadPdf}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  Statement PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Usage Item Component ────────────────────────────────────────────────────
function UsageItem({
  icon,
  label,
  usage,
  cost,
}: {
  icon: React.ReactNode;
  label: string;
  usage: string;
  cost: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-foreground-secondary">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-foreground">{label}</p>
        <p className="text-xs text-foreground-tertiary">{usage}</p>
      </div>
      <span className="text-sm text-foreground tabular-nums">${cost.toFixed(6)}</span>
    </div>
  );
}

// ─── Invoice Row ─────────────────────────────────────────────────────────────
function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const statusColors: Record<string, string> = {
    PAID: 'bg-success/10 text-success',
    PENDING: 'bg-warning/10 text-warning',
    FAILED: 'bg-error/10 text-error',
    VOID: 'bg-foreground-muted/10 text-foreground-muted',
  };

  const handleDownloadPdf = () => {
    billingApi.downloadInvoicePdf(invoice.id);
  };

  return (
    <tr className="hover:bg-background-hover transition-colors">
      <td className="py-3 px-4 text-sm text-foreground">
        {new Date(invoice.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </td>
      <td className="py-3 px-4 text-sm text-foreground-secondary">
        {invoice.description || 'Payment'}
      </td>
      <td className="py-3 px-4 text-sm text-foreground font-medium tabular-nums">
        ${(invoice.amount / 100).toFixed(2)}
      </td>
      <td className="py-3 px-4">
        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', statusColors[invoice.status])}>
          {invoice.status}
        </span>
      </td>
      <td className="py-3 px-4 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={handleDownloadPdf}
            className="p-1.5 hover:bg-background-hover rounded-md transition-colors text-foreground-secondary hover:text-accent"
            title="Download PDF"
          >
            <Download className="w-4 h-4" />
          </button>
          {invoice.invoiceUrl && (
            <a
              href={invoice.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 hover:bg-background-hover rounded-md transition-colors text-foreground-secondary hover:text-accent"
              title="View on Stripe"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </td>
    </tr>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Active Resources Widget – live cost ticker + balance runway
// ═══════════════════════════════════════════════════════════════════

function ActiveResourcesWidget() {
  const [data, setData] = useState<{
    deployments: any[];
    rentals: any[];
    summary: { totalActiveResources: number; totalBurnPerHour: number; balance: number; estimatedRunwayHours: number | null };
  } | null>(null);
  const [liveBurn, setLiveBurn] = useState(0);

  useEffect(() => {
    billingApi.getActiveResources().then(setData).catch(() => {});
    const interval = setInterval(() => {
      billingApi.getActiveResources().then(setData).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!data || data.summary.totalActiveResources === 0) return;
    const ratePerSec = data.summary.totalBurnPerHour / 3600;
    let accum = 0;
    const tick = setInterval(() => {
      accum += ratePerSec;
      setLiveBurn(accum);
    }, 1000);
    return () => clearInterval(tick);
  }, [data]);

  if (!data || data.summary.totalActiveResources === 0) return null;

  const formatRunway = (hours: number | null) => {
    if (hours === null) return 'N/A';
    if (hours > 720) return `${Math.floor(hours / 720)}mo`;
    if (hours > 24) return `${Math.floor(hours / 24)}d ${Math.floor(hours % 24)}h`;
    return `${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m`;
  };

  return (
    <div className="card p-6 border-green-500/20">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-green-500" />
        <h3 className="font-semibold text-foreground">Active Resources</h3>
        <span className="ml-auto text-xs text-foreground-tertiary font-mono animate-pulse">LIVE</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-xs text-foreground-tertiary">Resources</p>
          <p className="text-lg font-bold text-foreground">{data.summary.totalActiveResources}</p>
        </div>
        <div>
          <p className="text-xs text-foreground-tertiary">Burn Rate</p>
          <p className="text-lg font-bold text-foreground">${data.summary.totalBurnPerHour.toFixed(2)}/hr</p>
        </div>
        <div>
          <p className="text-xs text-foreground-tertiary">Since Loaded</p>
          <p className="text-lg font-bold text-green-400 font-mono">${liveBurn.toFixed(4)}</p>
        </div>
        <div>
          <p className="text-xs text-foreground-tertiary">Balance Runway</p>
          <p className="text-lg font-bold text-foreground">{formatRunway(data.summary.estimatedRunwayHours)}</p>
        </div>
      </div>

      <div className="space-y-2">
        {data.deployments.map(d => (
          <div key={d.id} className="flex items-center justify-between text-xs p-2 bg-background-secondary rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-foreground">{d.name}</span>
              {d.gpuType && <span className="text-foreground-tertiary">({d.gpuType})</span>}
            </div>
            <span className="font-mono text-foreground-secondary">${d.ratePerHour.toFixed(2)}/hr</span>
          </div>
        ))}
        {data.rentals.map(r => (
          <div key={r.id} className="flex items-center justify-between text-xs p-2 bg-background-secondary rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-foreground">{r.name}</span>
              <span className="text-foreground-tertiary">(Rental)</span>
            </div>
            <span className="font-mono text-foreground-secondary">${r.ratePerHour.toFixed(2)}/hr</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Billing Page (PAYG)
// ═══════════════════════════════════════════════════════════════════

export function BillingPage() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<BillingTab>('overview');
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [monthlyBilling, setMonthlyBilling] = useState<MonthlyBilling[]>([]);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Usage limit editing
  const [editingLimit, setEditingLimit] = useState(false);
  const [newLimit, setNewLimit] = useState<number>(50);

  // Auto-recharge
  const [autoRecharge, setAutoRecharge] = useState<AutoRechargeSettings | null>(null);
  const [editingAutoRecharge, setEditingAutoRecharge] = useState(false);
  const [arEnabled, setArEnabled] = useState(false);
  const [arThreshold, setArThreshold] = useState<number>(10);
  const [arAmount, setArAmount] = useState<number>(50);

  // Add funds amount
  const [selectedFundAmount, setSelectedFundAmount] = useState<number>(25);
  const [customAmount, setCustomAmount] = useState<string>('');

  const fetchBillingData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [accountResult, invoicesResult, pmResult, monthlyResult, arResult] = await Promise.allSettled([
        billingApi.getAccount(),
        billingApi.getInvoices(),
        billingApi.getPaymentMethod(),
        billingApi.getMonthlyBilling(),
        billingApi.getAutoRecharge(),
      ]);

      if (accountResult.status === 'fulfilled') {
        setAccount(accountResult.value);
        setNewLimit(accountResult.value.usageLimitUsd);
      } else {
        console.error('Failed to fetch account:', accountResult.reason);
      }

      if (invoicesResult.status === 'fulfilled') {
        setInvoices(invoicesResult.value.invoices);
      } else {
        console.error('Failed to fetch invoices:', invoicesResult.reason);
        setInvoices([]);
      }

      if (pmResult.status === 'fulfilled') {
        setPaymentMethod(pmResult.value.paymentMethod);
      } else {
        console.error('Failed to fetch payment method:', pmResult.reason);
        setPaymentMethod(null);
      }

      if (monthlyResult.status === 'fulfilled') {
        setMonthlyBilling(monthlyResult.value.months);
        const current = monthlyResult.value.months.find((m: MonthlyBilling) => m.isCurrent);
        if (current) setExpandedMonth(current.billingPeriod);
      } else {
        console.error('Failed to fetch monthly billing:', monthlyResult.reason);
        setMonthlyBilling([]);
      }

      if (arResult.status === 'fulfilled') {
        setAutoRecharge(arResult.value);
        setArEnabled(arResult.value.autoRechargeEnabled);
        setArThreshold(arResult.value.autoRechargeThreshold);
        setArAmount(arResult.value.autoRechargeAmount);
      } else {
        console.error('Failed to fetch auto-recharge:', arResult.reason);
      }

      const allFailed = [accountResult, invoicesResult, pmResult, monthlyResult].every(
        (r) => r.status === 'rejected'
      );
      if (allFailed) {
        setError('Failed to load billing data. Please check your connection and try again.');
      }
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError('Failed to load billing data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  // Handle URL params (success/canceled from Stripe Checkout for add-funds)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('funded') === 'true') {
      const amount = params.get('amount');
      const sessionId = params.get('session_id');
      window.history.replaceState({}, '', '/billing');

      if (sessionId) {
        billingApi.verifyPayment(sessionId)
          .then((result) => {
            setSuccessMsg(result.message || `$${amount || '?'} added to your account successfully!`);
            refreshUser();
            fetchBillingData();
          })
          .catch((err) => {
            console.error('Payment verification failed:', err);
            setError('Payment was processed but balance update failed. Please refresh the page or contact support.');
            refreshUser();
            fetchBillingData();
          });
      } else {
        setSuccessMsg(`$${amount || '?'} added to your account successfully!`);
        refreshUser();
        fetchBillingData();
      }
    }
    if (params.get('canceled') === 'true') {
      setError('Payment was canceled. No charges were made.');
      window.history.replaceState({}, '', '/billing');
    }
  }, [refreshUser, fetchBillingData]);

  const handleAddFunds = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedFundAmount;
    if (!amount || amount < 1) {
      setError('Minimum amount is $1');
      return;
    }
    if (amount > 10000) {
      setError('Maximum amount is $10,000');
      return;
    }

    setError(null);
    setSuccessMsg(null);
    setActionLoading('add-funds');

    try {
      const { url } = await billingApi.addFunds(amount);
      if (url) {
        window.location.href = url;
        return;
      }
      setError('Failed to create payment session');
    } catch (err: any) {
      setError(err?.message || 'Failed to add funds');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetUsageLimit = async () => {
    setError(null);
    setActionLoading('set-limit');

    try {
      await billingApi.setUsageLimit(newLimit);
      setSuccessMsg(`Usage limit updated to $${newLimit.toFixed(2)}/month`);
      setEditingLimit(false);
      await refreshUser();
      await fetchBillingData();
    } catch (err: any) {
      setError(err?.message || 'Failed to update usage limit');
    } finally {
      setActionLoading(null);
    }
  };

  const handleManagePaymentMethod = async () => {
    setError(null);
    try {
      const { url } = await billingApi.createPortalSession();
      if (url) {
        window.location.href = url;
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to open payment method manager';
      console.error('Failed to manage payment method:', err);
      setError(message);
    }
  };

  const handleSaveAutoRecharge = async () => {
    setError(null);
    setActionLoading('auto-recharge');

    try {
      const result = await billingApi.setAutoRecharge({
        enabled: arEnabled,
        threshold: arThreshold,
        amount: arAmount,
      });
      setSuccessMsg(result.message);
      setEditingAutoRecharge(false);
      setAutoRecharge({
        autoRechargeEnabled: result.autoRechargeEnabled,
        autoRechargeThreshold: result.autoRechargeThreshold,
        autoRechargeAmount: result.autoRechargeAmount,
        hasPaymentMethod: autoRecharge?.hasPaymentMethod ?? false,
        servicesSuspended: result.servicesSuspended,
      });
      if (result.balance !== undefined) {
        setAccount((prev) => prev ? { ...prev, balance: result.balance, servicesSuspended: result.servicesSuspended } : prev);
      }
      await refreshUser();
    } catch (err: any) {
      setError(err?.message || 'Failed to update auto-recharge settings');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeServices = async () => {
    setError(null);
    setActionLoading('resume');

    try {
      const result = await billingApi.resumeServices();
      setSuccessMsg(result.message);
      setAccount((prev) => prev ? { ...prev, servicesSuspended: false, servicesSuspendedAt: undefined } : prev);
      setAutoRecharge((prev) => prev ? { ...prev, servicesSuspended: false, servicesSuspendedAt: undefined } : prev);
      await refreshUser();
      await fetchBillingData();
    } catch (err: any) {
      setError(err?.message || 'Failed to resume services');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-32 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 skeleton rounded-lg" />
          ))}
        </div>
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  const balance = account?.balance ?? user?.balance ?? 0;
  const usageLimitUsd = account?.usageLimitUsd ?? user?.usageLimitUsd ?? 50;
  const currentMonthSpend = account?.currentMonthSpend ?? 0;
  const currentMonthEarnings = account?.currentMonthEarnings ?? 0;
  const totalEarnings = account?.totalEarnings ?? 0;
  const pendingEarnings = account?.pendingEarnings ?? 0;
  const spendPercent = usageLimitUsd > 0 ? (currentMonthSpend / usageLimitUsd) * 100 : 0;
  const stripeConfigured = account?.stripeConfigured ?? false;
  const currentMonthBilling = monthlyBilling.find((m) => m.isCurrent);
  const rates = account?.rates;
  const fmt6 = (n: number) => n.toFixed(6);

  const tabs: Array<{ id: BillingTab; label: string; icon: React.ReactNode }> = [
    { id: 'overview', label: 'Overview', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'history', label: 'Billing History', icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Billing</h1>
          <p className="text-foreground-secondary mt-1 text-sm">
            Pay-as-you-go billing. Add funds, track usage, and manage your spending limit.
          </p>
        </div>
        <button
          onClick={fetchBillingData}
          className="btn-ghost p-2 flex-shrink-0"
          title="Refresh billing data"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Success message */}
      {successMsg && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3 text-sm text-success animate-slide-down">
          <Check className="w-5 h-5 flex-shrink-0" />
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-auto text-success/70 hover:text-success">
            &times;
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-center gap-3 text-sm text-error animate-slide-down">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-error/70 hover:text-error">
            &times;
          </button>
        </div>
      )}

      {/* Service Suspension Banner */}
      {account?.servicesSuspended && (
        <div className="p-4 bg-error/10 border border-error/30 rounded-lg animate-slide-down">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-error">Services Suspended</h3>
              <p className="text-sm text-error/80 mt-1">
                All services associated with your account have been stopped because
                {usageLimitUsd > 0 && currentMonthSpend >= usageLimitUsd
                  ? ` you've reached your monthly spending limit of $${usageLimitUsd.toFixed(2)}.`
                  : ' your account has insufficient funds.'
                }
                {account.autoRechargeEnabled
                  ? ' Auto-recharge was unable to process payment. Please check your payment method.'
                  : ' Enable auto-recharge to prevent future service interruptions.'
                }
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleResumeServices}
                  disabled={actionLoading === 'resume'}
                  className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1.5"
                >
                  {actionLoading === 'resume' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Power className="w-3.5 h-3.5" />
                  )}
                  Resume Services
                </button>
                {usageLimitUsd > 0 && currentMonthSpend >= usageLimitUsd && (
                  <button
                    onClick={() => {
                      setEditingLimit(true);
                      setActiveTab('overview');
                    }}
                    className="btn-secondary text-sm py-1.5 px-3"
                  >
                    Increase Limit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-foreground-secondary hover:text-foreground hover:border-border'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Overview Tab ──────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Top cards: Balance + Spending + Payment Method */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Balance Card */}
            <div className="card p-6 border-accent/30">
              <div className="flex items-center gap-2 mb-4">
                <Wallet className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-foreground">Total Balance</h3>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums mb-1 break-all">
                ${fmt6(balance)}
              </div>
              <p className="text-xs text-foreground-tertiary">Prepaid balance for usage charges</p>
              {balance <= 5 && (
                <div className="mt-3 p-2 bg-warning/10 border border-warning/20 rounded-lg">
                  <p className="text-xs text-warning font-medium">
                    {balance <= 0 ? 'No funds remaining' : 'Low balance'} — add funds to continue using services
                  </p>
                </div>
              )}
            </div>

            {/* Monthly Spending Card */}
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-foreground-secondary" />
                <h3 className="font-semibold text-foreground">This Month Spend</h3>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums mb-1 break-all">
                ${fmt6(currentMonthSpend)}
              </div>
              {usageLimitUsd > 0 ? (
                <>
                  <p className="text-xs text-foreground-tertiary mb-3">
                    of ${usageLimitUsd.toFixed(2)} monthly limit
                  </p>
                  <div className="w-full h-2 bg-background-secondary rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full transition-all duration-300 rounded-full',
                        spendPercent >= 95 ? 'bg-error' : spendPercent >= 80 ? 'bg-warning' : 'bg-accent'
                      )}
                      style={{ width: `${Math.min(spendPercent, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-foreground-tertiary tabular-nums">
                      {spendPercent.toFixed(0)}% used
                    </span>
                    <span className="text-[10px] text-foreground-tertiary tabular-nums">
                      ${(usageLimitUsd - currentMonthSpend).toFixed(6)} remaining
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-foreground-tertiary">No spending limit set</p>
              )}
            </div>

            {/* Current Month Earnings Card */}
            <div className="card p-6 border-success/30">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-success" />
                <h3 className="font-semibold text-foreground">This Month Earnings</h3>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-success tabular-nums mb-1 break-all">
                ${fmt6(currentMonthEarnings)}
              </div>
              <p className="text-xs text-foreground-tertiary">From GPU marketplace rentals</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="p-2 bg-background-secondary rounded-lg">
                  <p className="text-[10px] text-foreground-tertiary">Total Earned</p>
                  <p className="text-sm font-semibold text-foreground tabular-nums">${fmt6(totalEarnings)}</p>
                </div>
                <div className="p-2 bg-background-secondary rounded-lg">
                  <p className="text-[10px] text-foreground-tertiary">Pending Payout</p>
                  <p className="text-sm font-semibold text-foreground tabular-nums">${fmt6(pendingEarnings)}</p>
                </div>
              </div>
            </div>

            {/* Payment method */}
            <PaymentMethodCard
              paymentMethod={paymentMethod}
              onManage={handleManagePaymentMethod}
              stripeConfigured={stripeConfigured}
            />

            {/* PAYG Verification Status */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-foreground-secondary" />
                  <h3 className="font-semibold text-foreground">PAYG Verification</h3>
                </div>
                {account?.paygVerified && (
                  <span className="badge-success text-[10px] px-1.5">Verified</span>
                )}
              </div>
              {account?.paygVerified ? (
                <div className="space-y-2">
                  <p className="text-sm text-success flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Your payment method is verified for automatic billing
                  </p>
                  <p className="text-xs text-foreground-tertiary">
                    Services can continue even when your prepaid balance is low. Your card will be charged automatically.
                  </p>
                  {account.paygVerifiedAt && (
                    <p className="text-xs text-foreground-tertiary">
                      Verified on {new Date(account.paygVerifiedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-foreground-secondary">
                    Set up PAYG to keep services running automatically when your balance is low.
                  </p>
                  <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                    <p className="text-xs text-warning">
                      Without PAYG verification, services will stop immediately when your balance runs out.
                      Add a card and enable auto-recharge to enable PAYG.
                    </p>
                  </div>
                  {stripeConfigured && !paymentMethod && (
                    <button
                      onClick={handleManagePaymentMethod}
                      className="btn-primary text-sm w-full"
                    >
                      Add Payment Method
                    </button>
                  )}
                  {paymentMethod && !account?.autoRechargeEnabled && (
                    <button
                      onClick={() => setEditingAutoRecharge(true)}
                      className="btn-primary text-sm w-full"
                    >
                      Enable Auto-Recharge
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Active Resources & Cost Estimation */}
          <ActiveResourcesWidget />

          {/* Add Funds Section */}
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Plus className="w-5 h-5 text-accent" />
              <h3 className="font-semibold text-foreground">Add Funds</h3>
            </div>

            {!stripeConfigured ? (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <p className="text-sm text-warning">
                  Stripe is not configured. Set a valid STRIPE_SECRET_KEY in your backend .env file to enable payments.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {(account?.fundAmounts || [10, 25, 50, 100, 250, 500]).map((amt) => (
                    <button
                      key={amt}
                      onClick={() => {
                        setSelectedFundAmount(amt);
                        setCustomAmount('');
                      }}
                      className={clsx(
                        'px-4 py-2 rounded-lg text-sm font-medium transition-colors border',
                        selectedFundAmount === amt && !customAmount
                          ? 'bg-accent text-white border-accent'
                          : 'bg-background-secondary text-foreground border-border hover:border-accent/50'
                      )}
                    >
                      ${amt}
                    </button>
                  ))}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-secondary">or</span>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-tertiary text-sm">$</span>
                      <input
                        type="number"
                        placeholder="Custom"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        min={1}
                        max={10000}
                        className="input pl-7 w-28 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleAddFunds}
                  disabled={actionLoading === 'add-funds'}
                  className="btn-primary"
                >
                  {actionLoading === 'add-funds' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4" />
                      Add ${customAmount || selectedFundAmount} to Balance
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Usage Limit Setting */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-foreground-secondary" />
                <h3 className="font-semibold text-foreground">Monthly Spending Limit</h3>
              </div>
              {!editingLimit && (
                <button onClick={() => setEditingLimit(true)} className="btn-ghost text-sm">
                  Edit
                </button>
              )}
            </div>

            {editingLimit ? (
              <div className="space-y-3">
                <p className="text-sm text-foreground-secondary">
                  Set a monthly cap on usage charges. Services will be paused when this limit is reached.
                  Set to $0 for no limit.
                </p>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-tertiary text-sm">$</span>
                    <input
                      type="number"
                      value={newLimit}
                      onChange={(e) => setNewLimit(parseFloat(e.target.value) || 0)}
                      min={0}
                      max={100000}
                      className="input pl-7 w-36"
                    />
                  </div>
                  <span className="text-sm text-foreground-tertiary">/month</span>
                  <button
                    onClick={handleSetUsageLimit}
                    disabled={actionLoading === 'set-limit'}
                    className="btn-primary text-sm"
                  >
                    {actionLoading === 'set-limit' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditingLimit(false);
                      setNewLimit(usageLimitUsd);
                    }}
                    className="btn-ghost text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-lg font-semibold text-foreground tabular-nums">
                  {usageLimitUsd > 0 ? `$${usageLimitUsd.toFixed(2)}/month` : 'No limit set'}
                </p>
                <p className="text-xs text-foreground-tertiary mt-1">
                  {usageLimitUsd > 0
                    ? 'Services will be paused when this limit is reached'
                    : 'Usage is unlimited — charges deducted from your balance'}
                </p>
              </div>
            )}
          </div>

          {/* Auto-Recharge Section */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-foreground-secondary" />
                <h3 className="font-semibold text-foreground">Auto-Recharge</h3>
                {autoRecharge?.autoRechargeEnabled && (
                  <span className="badge-accent text-[10px] px-1.5">Active</span>
                )}
              </div>
              {!editingAutoRecharge && (
                <button onClick={() => setEditingAutoRecharge(true)} className="btn-ghost text-sm">
                  Configure
                </button>
              )}
            </div>

            {editingAutoRecharge ? (
              <div className="space-y-4">
                <p className="text-sm text-foreground-secondary">
                  When enabled, your card will be automatically charged when your balance drops below the threshold.
                  This prevents service interruptions due to low balance.
                </p>

                {/* Enable/Disable toggle */}
                <div className="flex items-center justify-between p-3 bg-background-secondary rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-foreground">Enable Auto-Recharge</p>
                    <p className="text-xs text-foreground-tertiary mt-0.5">
                      Automatically charge your card when balance is low
                    </p>
                  </div>
                  <button
                    onClick={() => setArEnabled(!arEnabled)}
                    className="flex items-center"
                  >
                    {arEnabled ? (
                      <ToggleRight className="w-10 h-6 text-accent" />
                    ) : (
                      <ToggleLeft className="w-10 h-6 text-foreground-muted" />
                    )}
                  </button>
                </div>

                {arEnabled && (
                  <div className="space-y-3 pl-1">
                    <div>
                      <label className="label text-xs mb-1">When balance drops below</label>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-tertiary text-sm">$</span>
                          <input
                            type="number"
                            value={arThreshold}
                            onChange={(e) => setArThreshold(parseFloat(e.target.value) || 0)}
                            min={0}
                            max={10000}
                            className="input pl-7 w-32"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="label text-xs mb-1">Charge amount each time</label>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-tertiary text-sm">$</span>
                          <input
                            type="number"
                            value={arAmount}
                            onChange={(e) => setArAmount(parseFloat(e.target.value) || 0)}
                            min={5}
                            max={10000}
                            className="input pl-7 w-32"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-foreground-tertiary mt-1">
                        Minimum $5, maximum $10,000 per charge
                      </p>
                    </div>

                    {!autoRecharge?.hasPaymentMethod && (
                      <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                        <p className="text-xs text-warning font-medium">
                          A payment method is required. Please add a card first before enabling auto-recharge.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleSaveAutoRecharge}
                    disabled={actionLoading === 'auto-recharge'}
                    className="btn-primary text-sm"
                  >
                    {actionLoading === 'auto-recharge' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditingAutoRecharge(false);
                      setArEnabled(autoRecharge?.autoRechargeEnabled ?? false);
                      setArThreshold(autoRecharge?.autoRechargeThreshold ?? 10);
                      setArAmount(autoRecharge?.autoRechargeAmount ?? 50);
                    }}
                    className="btn-ghost text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {autoRecharge?.autoRechargeEnabled ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                      <p className="text-sm font-medium text-foreground">Auto-recharge is active</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="p-3 bg-background-secondary rounded-lg">
                        <p className="text-xs text-foreground-tertiary">Threshold</p>
                        <p className="text-sm font-semibold text-foreground mt-0.5 tabular-nums">
                          ${autoRecharge.autoRechargeThreshold.toFixed(2)}
                        </p>
                        <p className="text-[10px] text-foreground-tertiary mt-0.5">
                          Triggers when balance falls below this
                        </p>
                      </div>
                      <div className="p-3 bg-background-secondary rounded-lg">
                        <p className="text-xs text-foreground-tertiary">Charge Amount</p>
                        <p className="text-sm font-semibold text-foreground mt-0.5 tabular-nums">
                          ${autoRecharge.autoRechargeAmount.toFixed(2)}
                        </p>
                        <p className="text-[10px] text-foreground-tertiary mt-0.5">
                          Added to balance each time
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-foreground-secondary">
                      Auto-recharge is off. Enable it to automatically add funds when your balance gets low
                      and prevent service interruptions.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Current Month Usage */}
          {currentMonthBilling && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-foreground-secondary" />
                <h3 className="font-semibold text-foreground">Current Month Usage</h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <UsageStatCard
                  icon={<Cpu className="w-5 h-5" />}
                  label="GPU Compute"
                  value={`${currentMonthBilling.gpuComputeHours} hrs`}
                  cost={currentMonthBilling.gpuComputeCost}
                  rate="Varies by source"
                  color="accent"
                />
                <UsageStatCard
                  icon={<Zap className="w-5 h-5" />}
                  label="API Calls"
                  value={currentMonthBilling.apiCalls.toLocaleString()}
                  cost={currentMonthBilling.apiCallsCost}
                  rate={rates ? `$${rates.API_CALLS}/call` : ''}
                  color="success"
                />
                <UsageStatCard
                  icon={<HardDrive className="w-5 h-5" />}
                  label="Storage"
                  value={`${currentMonthBilling.storageGb} GB`}
                  cost={currentMonthBilling.storageCost}
                  rate={rates ? `$${rates.STORAGE}/GB` : ''}
                  color="warning"
                />
                <UsageStatCard
                  icon={<Wifi className="w-5 h-5" />}
                  label="Bandwidth"
                  value={`${currentMonthBilling.bandwidthGb} GB`}
                  cost={currentMonthBilling.bandwidthCost}
                  rate={rates ? `$${rates.BANDWIDTH}/GB` : ''}
                  color="error"
                />
              </div>
            </div>
          )}

          {/* PAYG Rates */}
          {rates && (
            <div className="card p-6">
              <h3 className="font-semibold text-foreground mb-3">Pay-As-You-Go Rates</h3>
              <p className="text-sm text-foreground-secondary mb-4">
                You only pay for what you use. No monthly subscription or base fee.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-background-secondary rounded-lg">
                  <p className="text-xs text-foreground-tertiary">GPU (Platform Deployments)</p>
                  <p className="text-sm font-semibold text-foreground mt-1">${rates.GPU_COMPUTE}/hr</p>
                </div>
                <div className="p-3 bg-background-secondary rounded-lg">
                  <p className="text-xs text-foreground-tertiary">API Calls</p>
                  <p className="text-sm font-semibold text-foreground mt-1">${rates.API_CALLS}/call</p>
                </div>
                <div className="p-3 bg-background-secondary rounded-lg">
                  <p className="text-xs text-foreground-tertiary">Storage</p>
                  <p className="text-sm font-semibold text-foreground mt-1">${rates.STORAGE}/GB</p>
                </div>
                <div className="p-3 bg-background-secondary rounded-lg">
                  <p className="text-xs text-foreground-tertiary">Bandwidth</p>
                  <p className="text-sm font-semibold text-foreground mt-1">${rates.BANDWIDTH}/GB</p>
                </div>
              </div>
              <p className="text-xs text-foreground-tertiary mt-3">
                Marketplace GPU rentals use seller-set rates which may differ. Prepaid rentals include a 10% discount.
              </p>
            </div>
          )}

          {/* Recent Transactions */}
          {invoices.length > 0 && (
            <div className="card overflow-hidden">
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-foreground-secondary" />
                  <h3 className="font-semibold text-foreground">Recent Transactions</h3>
                </div>
                <button
                  onClick={() => setActiveTab('history')}
                  className="btn-ghost text-sm"
                >
                  View All
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.slice(0, 5).map((invoice) => (
                      <InvoiceRow key={invoice.id} invoice={invoice} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Billing History Tab ──────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Monthly billing breakdown */}
          <div className="card overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-foreground-secondary" />
                <div>
                  <h2 className="font-semibold text-foreground">Monthly Usage History</h2>
                  <p className="text-xs text-foreground-tertiary mt-0.5">
                    Click a month to see usage details
                  </p>
                </div>
              </div>
            </div>

            {monthlyBilling.length > 0 ? (
              <div>
                {monthlyBilling.map((month) => (
                  <MonthlyBillingRow
                    key={month.billingPeriod}
                    month={month}
                    isExpanded={expandedMonth === month.billingPeriod}
                    onToggle={() =>
                      setExpandedMonth(
                        expandedMonth === month.billingPeriod ? null : month.billingPeriod
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Calendar className="w-10 h-10 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-secondary">No billing history yet</p>
                <p className="text-sm text-foreground-tertiary mt-1">
                  Usage data will appear here once you start using services
                </p>
              </div>
            )}
          </div>

          {/* All Transactions */}
          <div className="card overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-foreground-secondary" />
                <h2 className="font-semibold text-foreground">All Transactions</h2>
              </div>
              {invoices.length > 0 && (
                <span className="text-xs text-foreground-tertiary">{invoices.length} transactions</span>
              )}
            </div>

            {invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <InvoiceRow key={invoice.id} invoice={invoice} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-10 h-10 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-secondary">No transactions yet</p>
                <p className="text-sm text-foreground-tertiary mt-1">
                  Your payment history will appear here
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Usage Stat Card ─────────────────────────────────────────────────────────
function UsageStatCard({
  icon,
  label,
  value,
  cost,
  rate,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  cost: number;
  rate: string;
  color: string;
}) {
  return (
    <div className="p-4 rounded-lg bg-background-secondary">
      <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center mb-3', `text-${color}`, `bg-${color}/10`)}>
        {icon}
      </div>
      <p className="text-xs text-foreground-tertiary">{label}</p>
      <p className="text-lg font-semibold text-foreground mt-0.5 tabular-nums">{value}</p>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-foreground-secondary tabular-nums">${cost.toFixed(6)}</p>
        {rate && <p className="text-[10px] text-foreground-tertiary">{rate}</p>}
      </div>
    </div>
  );
}
